import React, { useEffect, useContext, useState, useRef } from 'react';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { toJS } from 'mobx';
import { useObserver } from 'mobx-react-lite';
import { Mention, MentionsInput } from 'react-mentions';

import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus } from '../../../../_shared/QuickDrawer';

import WorkOrderTemplateUpdateStore from '../../../../../../stores/WorkOrderTemplateUpdateStore';
import WorkOrderControlViewStore from '../../../../../../stores/WorkOrderControlViewStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as oh from '../../../../../../utilities/operationHelper';
import * as tch from '../../../../../../utilities/templateControlHelper';
import * as mh from '../../../../../../utilities/mentionHelper';

import './UpdateExistingWorkOrderControl.scss';
import api from '../../../../../../api';

function UpdateExistingWorkOrderControl(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const workOrderTemplate = useContext(WorkOrderTemplateUpdateStore);
    const workOrderControl = useContext(WorkOrderControlViewStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [size, setSize] = useState('');
    const [align, setAlign] = useState('');
    const [valign, setValign] = useState('');
    const [equation, setEquation] = useState('');
    const [timestampDestination, setTimestampDestination] = useState('');
    const [setFieldsMapping, setSetFieldsMapping] = useState([]);
    const [pretestTypes, setPretestTypes] = useState(null);
    const [pretestOptionType, setPretestOptionType] = useState('');
    const [pretestOptionIsMultiple, setPretestOptionIsMultiple] = useState(false);
    const [pretestOptionScaleToFit, setPretestOptionScaleToFit] = useState(false);
    const [prescriptionTypes, setPrescriptionTypes] = useState(null);
    const [prescriptionTemplates, setPrescriptionTemplates] = useState(null);
    const [prescriptionCreateType, setPrescriptionCreateType] = useState('');
    const [prescriptionCreateMapping, setPrescriptionCreateMapping] = useState([]);
    const [prescriptionCopyType, setPrescriptionCopyType] = useState('');
    const [prescriptionCopyMapping, setPrescriptionCopyMapping] = useState([]);
    const [prescriptionListType, setPrescriptionListType] = useState('');
    const [prescriptionListMode, setPrescriptionListMode] = useState('all');
    const [prescriptionSummaryMode, setPrescriptionSummaryMode] = useState('all');
    const [prescriptionSummaryMultiple, setPrescriptionSummaryMultiple] = useState(true);
    const [productTypes, setProductTypes] = useState([]);
    const [productCopyType, setProductCopyType] = useState('');
    const [productCopyMapping, setProductCopyMapping] = useState([]);
    const [options, setOptions] = useState([]);
    const [inline, setInline] = useState(false);
    const [inputOptions, setInputOptions] = useState([]);

    useEffect(() => {
        if (props.extraProps.inputOptions && props.extraProps.inputOptions.length > 0) {
            setInputOptions(toJS(props.extraProps.inputOptions));
        }

        focusTimer.current = setTimeout(() => {
            setSize(workOrderControl.getMetadata('size'));
            setAlign(workOrderControl.getMetadata('align'));
            setValign(workOrderControl.getMetadata('valign'));
            setOptions(workOrderControl.getMetadata('options'));
            setInline(workOrderControl.getMetadata('inline'));
            setEquation(workOrderControl.getMetadata('equation'));
            setTimestampDestination(workOrderControl.getMetadata('timestamp'));

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRETEST_OPTIONS)) {
                const pretestOption = workOrderControl.getMetadata('pretest');

                if (pretestOption) {
                    setPretestOptionType(pretestOption.type);
                    setPretestOptionIsMultiple(pretestOption.isMultiple);
                    setPretestOptionScaleToFit(pretestOption.scaleToFit);
                }

                loadPretestData();
            }

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_CREATE_OPTIONS)) {
                const prescriptionCreateOption = workOrderControl.getMetadata('prescriptionCreate');

                if (prescriptionCreateOption) {
                    setPrescriptionCreateType(prescriptionCreateOption.type);
                    setPrescriptionCreateMapping(prescriptionCreateOption.mapping);
                }

                loadPrescriptionData();
            }

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_COPY_OPTIONS)) {
                const prescriptionCopyOption = workOrderControl.getMetadata('prescriptionCopy');

                if (prescriptionCopyOption) {
                    setPrescriptionCopyType(prescriptionCopyOption.type);
                    setPrescriptionCopyMapping(prescriptionCopyOption.mapping);
                }

                loadPrescriptionData();
            }

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_LIST_OPTIONS)) {
                const prescriptionListOption = workOrderControl.getMetadata('prescriptionList');

                if (prescriptionListOption) {
                    setPrescriptionListType(prescriptionListOption.type);
                    setPrescriptionListMode(prescriptionListOption.mode ? prescriptionListOption.mode : 'all');
                }

                loadPrescriptionData();
            }

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_SUMMARY_OPTIONS)) {
                const prescriptionSummaryOption = workOrderControl.getMetadata('prescriptionSummary');

                if (prescriptionSummaryOption) {
                    setPrescriptionSummaryMode(prescriptionSummaryOption.mode ? prescriptionSummaryOption.mode : 'all');
                    setPrescriptionSummaryMultiple(prescriptionSummaryOption.multiple ? prescriptionSummaryOption.multiple : true);
                }

                loadPrescriptionData();
            }

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRODUCT_COPY_OPTIONS)) {
                const productCopyOption = workOrderControl.getMetadata('productCopy');

                if (productCopyOption) {
                    setProductCopyType(productCopyOption.type);
                    setProductCopyMapping(productCopyOption.mapping);
                }

                loadProductData();
            }

            if (tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_SET_FIELDS_OPTIONS)) {
                const setFieldsOption = workOrderControl.getMetadata('setFields');

                if (setFieldsOption) {
                    setSetFieldsMapping(setFieldsOption.mapping);
                }
            }

            quickDrawerFocus(props.drawer);
        }, 100)

        return (() => {
            isMounted.current = false;
            workOrderControl.clear();
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const loadPretestData = () => {
        api.PretestPublishedTemplates.all()
            .then(({ data }) => {
                if (isMounted.current) {
                    const sortedPretestTypes = data.map(d => { return d.pretestType }).sort((a, b) => a.displayOrder - b.displayOrder);
                    setPretestTypes(sortedPretestTypes);
                }
            })
    }

    const loadPrescriptionData = () => {
        api.PrescriptionPublishedTemplates.all()
            .then(({ data }) => {
                if (isMounted.current) {
                    const sortedPrescriptionTypes = data.map(d => { return d.prescriptionType }).sort((a, b) => a.displayOrder - b.displayOrder);
                    setPrescriptionTemplates(data);
                    setPrescriptionTypes(sortedPrescriptionTypes);
                }
            })
    }

    const loadProductData = () => {
        api.ProductTypes.all()
            .then(({ data }) => {
                if (isMounted.current) {
                    setProductTypes(data.filter(d => workOrderTemplate.data.productTypes.some(t => t.id === d.id)));
                }
            })
    }

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (workOrderControl.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();
        handlePopulateMetadata();
        workOrderControl.save()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, data);
                    }
                }
            })
    }

    const handleLabelChange = event => {
        workOrderControl.data.label = event.target.value;
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleTextChange = event => {
        workOrderControl.data.text = event.target.value;
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleSizeChange = event => {
        setSize(event.target.value);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleAlignChange = value => {
        setAlign((value ? value : 'left'));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleValignChange = value => {
        setValign((value ? value : 'top'));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleInlineChange = event => {
        setInline((event.target.value === 'true'));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleEquationChange = (event, newValue) => {
        setEquation(newValue);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleTimestampChange = (event, newValue) => {
        setTimestampDestination(newValue);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePretestOptionTypeChange = (event) => {
        setPretestOptionType(event.target.value);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePretestOptionIsMultipleChange = event => {
        setPretestOptionIsMultiple((event.target.value === 'true'));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePretestOptionScaleToFitChange = event => {
        setPretestOptionScaleToFit((event.target.value === 'true'));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionCreateTypeChange = (event) => {
        const id = event.target.value;
        setPrescriptionCreateType(id);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionCreateMappingSourceChange = (event, index) => {
        const tempPrescriptionCreateMapping = [...prescriptionCreateMapping];

        tempPrescriptionCreateMapping[index].source = event.target.value;
        setPrescriptionCreateMapping(tempPrescriptionCreateMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionCreateMappingDestinationChange = (event, index) => {
        const tempPrescriptionCreateMapping = [...prescriptionCreateMapping];

        tempPrescriptionCreateMapping[index].destination = event.target.value;
        setPrescriptionCreateMapping(tempPrescriptionCreateMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionCopyTypeChange = (event) => {
        const id = event.target.value;
        setPrescriptionCopyType(id);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionCopyMappingSourceChange = (event, index) => {
        const tempPrescriptionCopyMapping = [...prescriptionCopyMapping];

        tempPrescriptionCopyMapping[index].source = event.target.value;
        setPrescriptionCopyMapping(tempPrescriptionCopyMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionCopyMappingDestinationChange = (event, index) => {
        const tempPrescriptionCopyMapping = [...prescriptionCopyMapping];

        tempPrescriptionCopyMapping[index].destination = event.target.value;
        setPrescriptionCopyMapping(tempPrescriptionCopyMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleProductCopyTypeChange = (event) => {
        const id = event.target.value;
        setProductCopyType(id);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleProductCopyMappingSourceChange = (event, index) => {
        const tempProductCopyMapping = [...productCopyMapping];

        tempProductCopyMapping[index].source = event.target.value;
        setProductCopyMapping(tempProductCopyMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleProductCopyMappingDestinationChange = (event, index) => {
        const tempProductCopyMapping = [...productCopyMapping];

        tempProductCopyMapping[index].destination = event.target.value;
        setProductCopyMapping(tempProductCopyMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionListTypeChange = (event) => {
        const id = event.target.value;
        setPrescriptionListType(id);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionListModeChange = (event, mode) => {
        setPrescriptionListMode(mode);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionSummaryModeChange = (event, mode) => {
        setPrescriptionSummaryMode(mode);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePrescriptionSummaryMultipleChange = (event, multiple) => {
        setPrescriptionSummaryMultiple(multiple);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleSetFieldMappingKeyChange = (event, index) => {
        const tempSetFieldsMapping = [...setFieldsMapping];

        tempSetFieldsMapping[index].key = event.target.value;
        setSetFieldsMapping(tempSetFieldsMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleSetFieldMappingValueChange = (event, index) => {
        const tempSetFieldMapping = [...setFieldsMapping];

        tempSetFieldMapping[index].value = event.target.value;
        setSetFieldsMapping(tempSetFieldMapping);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleAddSetFieldMapping = event => {
        event.preventDefault();
        setSetFieldsMapping(p => [...setFieldsMapping, { key: '', value: '' }]);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#new-work-order-control-set-field-${(setFieldsMapping.length)}`)
            if (element) { element.focus(); }
        }, 50);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleRemoveSetFieldMapping = (mapping, index) => {
        setSetFieldsMapping(setFieldsMapping.filter((o, oi) => oi !== index));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handlePopulateMetadata = () => {
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_SIZE, 'size', (size ? size.trim() : null));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_ALIGN, 'align', (align ? align.trim() : null));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_VALIGN, 'valign', (valign ? valign.trim() : null));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_OPTIONS, 'options', options);
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_INLINE, 'inline', inline);
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_EQUATION, 'equation', (equation ? equation.trim() : null));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_TIMESTAMP_DESTINATION, 'timestamp', (timestampDestination ? timestampDestination.trim() : null));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_PRETEST_OPTIONS, 'pretest', (
            pretestOptionType && pretestOptionIsMultiple !== null ? {
                type: pretestOptionType,
                isMultiple: pretestOptionIsMultiple,
                scaleToFit: pretestOptionScaleToFit,
            } : null
        ));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_CREATE_OPTIONS, 'prescriptionCreate', (
            prescriptionCreateType && prescriptionCreateMapping !== null ? {
                type: prescriptionCreateType,
                mapping: prescriptionCreateMapping,
            } : null
        ));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_COPY_OPTIONS, 'prescriptionCopy', (
            prescriptionCopyType && prescriptionCopyMapping !== null ? {
                type: prescriptionCopyType,
                mapping: prescriptionCopyMapping,
            } : null
        ));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_LIST_OPTIONS, 'prescriptionList', (
            prescriptionListType && prescriptionListMode ? {
                type: prescriptionListType,
                mode: prescriptionListMode,
            } : null
        ));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_SUMMARY_OPTIONS, 'prescriptionSummary', (
            prescriptionSummaryMode ? {
                mode: prescriptionSummaryMode,
                multiple: !!prescriptionSummaryMultiple,
            } : null
        ));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_PRODUCT_COPY_OPTIONS, 'productCopy', (
            productCopyType && productCopyMapping !== null ? {
                type: productCopyType,
                mapping: productCopyMapping,
            } : null
        ));
        populateMetadata(tch.TEMPLATE_CONTROL_METADATA_SET_FIELDS_OPTIONS, 'setFields', (
            setFieldsMapping !== null ? {
                mapping: setFieldsMapping,
            } : null
        ));
    }

    const handleAddPrescriptionCreateMapping = event => {
        event.preventDefault();
        setPrescriptionCreateMapping(p => [...prescriptionCreateMapping, { source: '', destination: '' }]);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#new-work-order-control-prescription-create-source-${(prescriptionCreateMapping.length)}`)
            if (element) { element.focus(); }
        }, 50);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleRemovePrescriptionCreateMapping = (mapping, index) => {
        setPrescriptionCreateMapping(prescriptionCreateMapping.filter((o, oi) => oi !== index));
        workOrderControl.hasUnsavedChanges = true;
    }

    const renderPrescriptionCreateMapping = () => {
        return <>
            {
                prescriptionCreateMapping && prescriptionCreateMapping.length > 0 ?
                    prescriptionCreateMapping.map((o, oi) => {
                        return <ul
                            key={`work-order_input_prescription_mapping_${oi}`}
                            className='list-inline no-style m-0 mt-n2 mb-2'
                        >
                            <li className='list-inline-item m-0 option-sm'>
                                <MentionsInput
                                    singleLine
                                    id={`new-work-order-control-prescription-create-source-${oi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={o.source ? o.source : ''}
                                    title={mh.getDisplay(o.source)}
                                    onChange={e => { handlePrescriptionCreateMappingSourceChange(e, oi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={inputOptions}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item mx-2'>to</li>
                            <li className='list-inline-item m-0 option-sm'>
                                <MentionsInput
                                    singleLine
                                    id={`new-work-order-control-prescription-create-destination-${oi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={o.destination ? o.destination : ''}
                                    title={mh.getDisplay(o.destination)}
                                    onChange={e => { handlePrescriptionCreateMappingDestinationChange(e, oi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={(prescriptionTemplates && prescriptionTemplates.length > 0 && prescriptionCreateType ?
                                            prescriptionTemplates.filter(t => t.prescriptionTypeId === prescriptionCreateType)[0].definition.definition
                                                .filter(d => d.key && d.name)
                                                .map(d => {
                                                    return {
                                                        id: d.key,
                                                        display: d.name
                                                    }
                                                }) : null)}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemovePrescriptionCreateMapping(o, oi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddPrescriptionCreateMapping}
            >
                <i className='fal fa-plus-circle'></i> Add mapping
            </button>
        </>
    }

    const handleAddPrescriptionCopyMapping = event => {
        event.preventDefault();
        setPrescriptionCopyMapping(p => [...prescriptionCopyMapping, { source: '', destination: '' }]);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#new-work-order-control-prescription-copy-source-${(prescriptionCopyMapping.length)}`)
            if (element) { element.focus(); }
        }, 50);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleRemovePrescriptionCopyMapping = (mapping, index) => {
        setPrescriptionCopyMapping(prescriptionCopyMapping.filter((o, oi) => oi !== index));
        workOrderControl.hasUnsavedChanges = true;
    }

    const renderPrescriptionCopyMapping = () => {
        return <>
            {
                prescriptionCopyMapping && prescriptionCopyMapping.length > 0 ?
                    prescriptionCopyMapping.map((o, oi) => {
                        return <ul
                            key={`work-order_input_prescription_mapping_${oi}`}
                            className='list-inline no-style m-0 mt-n2 mb-2'
                        >
                            <li className='list-inline-item m-0 option-sm'>
                                <MentionsInput
                                    singleLine
                                    id={`new-work-order-control-prescription-copy-source-${oi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={o.source ? o.source : ''}
                                    title={mh.getDisplay(o.source)}
                                    onChange={e => { handlePrescriptionCopyMappingSourceChange(e, oi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={(prescriptionTemplates && prescriptionTemplates.length > 0 && prescriptionCopyType ?
                                            prescriptionTemplates.filter(t => t.prescriptionTypeId === prescriptionCopyType)[0].definition.definition
                                                .filter(d => d.key && d.name)
                                                .map(d => {
                                                    return {
                                                        id: d.key,
                                                        display: d.name
                                                    }
                                                }) : null)}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item mx-2'>to</li>
                            <li className='list-inline-item m-0 option-sm'>
                                <MentionsInput
                                    singleLine
                                    id={`new-work-order-control-prescription-copy-destination-${oi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={o.destination ? o.destination : ''}
                                    title={mh.getDisplay(o.destination)}
                                    onChange={e => { handlePrescriptionCopyMappingDestinationChange(e, oi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={inputOptions}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemovePrescriptionCopyMapping(o, oi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddPrescriptionCopyMapping}
            >
                <i className='fal fa-plus-circle'></i> Add mapping
            </button>
        </>
    }

    const handleAddProductCopyMapping = event => {
        event.preventDefault();
        setProductCopyMapping(p => [...productCopyMapping, { source: '', destination: '' }]);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#new-work-order-control-product-copy-source-${(productCopyMapping.length)}`)
            if (element) { element.focus(); }
        }, 50);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleRemoveProductCopyMapping = (mapping, index) => {
        setProductCopyMapping(productCopyMapping.filter((o, oi) => oi !== index));
        workOrderControl.hasUnsavedChanges = true;
    }

    const renderProductCopyMapping = () => {
        return <>
            {
                productCopyMapping && productCopyMapping.length > 0 ?
                    productCopyMapping.map((o, oi) => {
                        return <ul
                            key={`work-order_input_product_mapping_${oi}`}
                            className='list-inline no-style m-0 mt-n2 mb-2'
                        >
                            <li className='list-inline-item m-0 option-sm'>
                                <MentionsInput
                                    singleLine
                                    id={`new-work-order-control-product-copy-source-${oi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={o.source ? o.source : ''}
                                    title={mh.getDisplay(o.source)}
                                    onChange={e => { handleProductCopyMappingSourceChange(e, oi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={(productTypes && productTypes.length > 0 && productCopyType ?
                                            productTypes.filter(t => t.id === productCopyType)[0].data
                                                .filter(d => d.key && d.name)
                                                .map(d => {
                                                    return {
                                                        id: d.key,
                                                        display: d.name
                                                    }
                                                }) : null)}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item mx-2'>to</li>
                            <li className='list-inline-item m-0 option-sm'>
                                <MentionsInput
                                    singleLine
                                    id={`new-work-order-control-product-copy-destination-${oi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={o.destination ? o.destination : ''}
                                    title={mh.getDisplay(o.destination)}
                                    onChange={e => { handleProductCopyMappingDestinationChange(e, oi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={inputOptions}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemoveProductCopyMapping(o, oi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddProductCopyMapping}
            >
                <i className='fal fa-plus-circle'></i> Add mapping
            </button>
        </>
    }

    const handleOptionChange = (event, index) => {
        const tempOptions = [...options];
        tempOptions[index] = event.target.value;
        setOptions(tempOptions);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleAddOption = event => {
        event.preventDefault();
        setOptions(o => [...options, '']);
        focusTimer.current = setTimeout(() => {
            const element = document.querySelector(`#new-work-order-control-${workOrderControl.data.key}-${(options.length)}`)
            if (element) { element.focus(); }
        }, 50);
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleRemoveOption = (option, index) => {
        setOptions(options.filter((o, oi) => oi !== index));
        workOrderControl.hasUnsavedChanges = true;
    }

    const handleMoveOption = (from, to) => {
        const tempOptions = [...options];
        const element = tempOptions[from];

        tempOptions.splice(from, 1);
        tempOptions.splice(to, 0, element);

        setOptions(tempOptions);
        workOrderControl.hasUnsavedChanges = true;
    }

    const renderSetFieldMapping = () => {
        return <>
            {
                setFieldsMapping && setFieldsMapping.length > 0 ?
                    setFieldsMapping.map((f, fi) => {
                        return <ul
                            key={`prescription_input_set_field_mapping_${fi}`}
                            className='list-inline no-style m-0 mt-n2 mb-2'
                        >
                            <li className='list-inline-item m-0 option-xs'>
                                <select
                                    id={`new-prescription-control-set-field-key-${fi}`}
                                    className='custom-select custom-select-sm form-control'
                                    value={f.key ? f.key : ''}
                                    onChange={e => { handleSetFieldMappingKeyChange(e, fi) }}
                                >
                                    <option></option>
                                    {
                                        inputOptions && inputOptions.length > 0 ?
                                            inputOptions.map((o, oi) => {
                                                return <option
                                                    key={`new-prescription-control-set-field-key-${fi}-${oi}`}
                                                    value={o.id}
                                                >{o.display}</option>
                                            }) : null
                                    }
                                </select>
                            </li>
                            <li className='list-inline-item mx-2'>=</li>
                            <li className='list-inline-item m-0 option-md'>
                                <MentionsInput
                                    singleLine
                                    id={`new-prescription-control-set-field-value-${fi}`}
                                    className='react-mentions-wrapper'
                                    autoComplete='off'
                                    value={f.value ? f.value : ''}
                                    onChange={e => { handleSetFieldMappingValueChange(e, fi) }}
                                >
                                    <Mention
                                        trigger='#'
                                        data={inputOptions}
                                        markup={mh.MENTION_MARKUP}
                                    />
                                </MentionsInput>
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemoveSetFieldMapping(f, fi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddSetFieldMapping}
            >
                <i className='fal fa-plus-circle'></i> Add mapping
            </button>
        </>
    }

    const renderOptions = () => {
        return <>
            {
                options && options.length > 0 ?
                    options.map((o, oi) => {
                        return <ul
                            key={`work-order_input_options_${oi}`}
                            className='list-inline no-style m-0 mb-2'
                        >
                            <li className='list-inline-item m-0 mr-1'>{oi + 1}.&nbsp;</li>
                            <li className='list-inline-item m-0 option'>
                                <input
                                    id={`update-work-order-control-${workOrderControl.data.key}-${oi}`}
                                    type='text'
                                    className='form-control form-control-sm'
                                    autoComplete='off'
                                    value={o}
                                    onChange={(e) => handleOptionChange(e, oi)}
                                />
                            </li>
                            <li className='list-inline-item m-0 ml-2'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    onClick={e => { handleRemoveOption(o, oi) }}
                                >
                                    <i className={'fal fa-minus-circle text-danger'}></i>
                                </button>
                            </li>
                            <li className='list-inline-item m-0'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    autoComplete='off'
                                    disabled={options.length === 1 || oi === 0}
                                    onClick={e => { handleMoveOption(oi, (oi - 1)) }}
                                >
                                    <i className={'fal fa-arrow-up' + (options.length === 1 || oi === 0 ? ' text-gray-500' : ' text-primary')}></i>
                                </button>
                            </li>
                            <li className='list-inline-item m-0'>
                                <button
                                    type='button'
                                    className='btn btn-link btn-icon btn-sm'
                                    disabled={options.length === 1 || oi === (options.length - 1)}
                                    onClick={e => { handleMoveOption(oi, (oi + 1)) }}
                                >
                                    <i className={'fal fa-arrow-down' + (options.length === 1 || oi === (options.length - 1) ? ' text-gray-500' : ' text-primary')}></i>
                                </button>
                            </li>
                        </ul>
                    }) : <div></div>
            }
            <button
                type='button'
                className='btn btn-link btn-icon-left btn-sm'
                onClick={handleAddOption}
            >
                <i className='fal fa-plus-circle'></i> Add option
            </button>
        </>
    }

    const populateMetadata = (field, key, value) => {
        if (tch.hasField(workOrderControl.data.type, field)) {
            workOrderControl.setMetadata(key, value);
        } else {
            workOrderControl.removeMetadata(key);
        }
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={workOrderControl.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('work-order-input', 'control')}
                        action='Update'
                        category={tch.getName(workOrderControl.data.type)}
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <FadeIn>
                            <div className='update-work-order-control body-content'>
                                <section>
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_LABEL) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='new-work-order-control-label'><small>Label</small></label>
                                                        <input
                                                            id='new-work-order-control-label'
                                                            type='text'
                                                            maxLength={100}
                                                            className='form-control'
                                                            value={workOrderControl.data.label ? workOrderControl.data.label : ''}
                                                            autoComplete={'off'}
                                                            onChange={handleLabelChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_TEXT) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label htmlFor='new-work-order-control-text'><small>Text</small></label>
                                                        <input
                                                            id='new-work-order-control-text'
                                                            type='text'
                                                            maxLength={100}
                                                            className='form-control'
                                                            value={workOrderControl.data.text ? workOrderControl.data.text : ''}
                                                            autoComplete={'off'}
                                                            onChange={handleTextChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_SIZE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='new-work-order-control-size'><small>Size</small></label>
                                                        <select
                                                            id='new-work-order-control-size'
                                                            className='custom-select form-control'
                                                            value={size ? size : ''}
                                                            onChange={handleSizeChange}
                                                        >
                                                            <option value='sm'>Small</option>
                                                            <option value=''>Standard</option>
                                                            <option value='lg'>Large</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_ALIGN) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='new-work-order-control-align'><small>Align</small></label>
                                                        <div>
                                                            <div className='btn-group'>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (align === 'left' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('left') }}
                                                                >
                                                                    <i className='fal fa-align-left'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (align === 'center' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('center') }}
                                                                >
                                                                    <i className='fal fa-align-center'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (align === 'right' ? ' active' : '')}
                                                                    value={(align ? align : '')}
                                                                    onClick={() => { handleAlignChange('right') }}
                                                                >
                                                                    <i className='fal fa-align-right'></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_VALIGN) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='new-work-order-control-valign'><small>Vertical Align</small></label>
                                                        <div>
                                                            <div className='btn-group'>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (valign === 'top' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('top') }}
                                                                >
                                                                    <i className='fal fa-arrow-to-top'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (valign === 'middle' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('middle') }}
                                                                >
                                                                    <i className='fal fa-horizontal-rule'></i>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={'btn btn-secondary' + (valign === 'bottom' ? ' active' : '')}
                                                                    value={(valign ? valign : '')}
                                                                    onClick={() => { handleValignChange('bottom') }}
                                                                >
                                                                    <i className='fal fa-arrow-to-bottom'></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_INLINE) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label><small>Inline</small></label>
                                                        <div>
                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                <input
                                                                    id={`new-work-order-control-inline-true`}
                                                                    type='radio'
                                                                    name='work-order-control-inline'
                                                                    className='custom-control-input'
                                                                    value={true}
                                                                    checked={inline}
                                                                    onChange={handleInlineChange}
                                                                />
                                                                <label
                                                                    htmlFor={`new-work-order-control-inline-true`}
                                                                    className='custom-control-label'
                                                                >
                                                                    Yes
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                <input
                                                                    id={`new-work-order-control-inline-false`}
                                                                    type='radio'
                                                                    name='work-order-control-inline'
                                                                    className='custom-control-input'
                                                                    value={false}
                                                                    checked={!inline}
                                                                    onChange={handleInlineChange}
                                                                />
                                                                <label
                                                                    htmlFor={`new-work-order-control-inline-false`}
                                                                    className='custom-control-label'
                                                                >
                                                                    No
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_OPTIONS) ?
                                            <div className='row options'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='new-work-order-control-options'><small>Options</small></label>
                                                        {renderOptions()}
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_EQUATION) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='new-work-order-control-equation'><small>Equation</small></label>
                                                        <MentionsInput
                                                            singleLine
                                                            className='react-mentions-wrapper'
                                                            value={equation ? equation : ''}
                                                            onChange={handleEquationChange}
                                                        >
                                                            <Mention
                                                                trigger='#'
                                                                data={inputOptions}
                                                                markup={mh.MENTION_MARKUP}
                                                            />
                                                        </MentionsInput>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_TIMESTAMP_DESTINATION) ?
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-4'>
                                                        <label className='required' htmlFor='new-work-order-control-timestamp'><small>Map to</small></label>
                                                        <MentionsInput
                                                            singleLine
                                                            className='react-mentions-wrapper'
                                                            value={timestampDestination ? timestampDestination : ''}
                                                            onChange={handleTimestampChange}
                                                        >
                                                            <Mention
                                                                trigger='#'
                                                                data={inputOptions}
                                                                markup={mh.MENTION_MARKUP}
                                                            />
                                                        </MentionsInput>
                                                    </div>
                                                </div>
                                            </div> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRETEST_OPTIONS) ?
                                            <>
                                                {
                                                    pretestTypes && pretestTypes.length > 0 ?
                                                        <FadeIn>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-pretest-type'><small>Type</small></label>
                                                                        <select
                                                                            id='new-work-order-pretest-type'
                                                                            className='custom-select form-control'
                                                                            value={pretestOptionType ? pretestOptionType : ''}
                                                                            onChange={handlePretestOptionTypeChange}
                                                                        >
                                                                            <option value=''></option>
                                                                            {
                                                                                pretestTypes ?
                                                                                    pretestTypes.map((t, ti) => {
                                                                                        return <option
                                                                                            key={`new_work_order_pretest_type_${ti}`}
                                                                                            value={t.id}
                                                                                        >{t.name}</option>
                                                                                    }) : null
                                                                            }
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-pretest-multi'><small>Allow Multiple Import</small></label>
                                                                        <div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-work-order-pretest-multi-true`}
                                                                                    type='radio'
                                                                                    name='new-work-order-pretest-multi'
                                                                                    className='custom-control-input'
                                                                                    value={true}
                                                                                    checked={pretestOptionIsMultiple}
                                                                                    onChange={handlePretestOptionIsMultipleChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-work-order-pretest-multi-true`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-work-order-pretest-multi-false`}
                                                                                    type='radio'
                                                                                    name='new-work-order-pretest-multi'
                                                                                    className='custom-control-input'
                                                                                    value={false}
                                                                                    checked={!pretestOptionIsMultiple}
                                                                                    onChange={handlePretestOptionIsMultipleChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-work-order-pretest-multi-false`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className='row'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-pretest-scale'><small>Scale To Fit</small></label>
                                                                        <div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-work-order-pretest-scale-true`}
                                                                                    type='radio'
                                                                                    name='new-work-order-pretest-scale'
                                                                                    className='custom-control-input'
                                                                                    value={true}
                                                                                    checked={pretestOptionScaleToFit}
                                                                                    onChange={handlePretestOptionScaleToFitChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-work-order-pretest-scale-true`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    Yes
                                                                                </label>
                                                                            </div>
                                                                            <div className='custom-control custom-radio custom-control-inline'>
                                                                                <input
                                                                                    id={`new-work-order-pretest-scale-false`}
                                                                                    type='radio'
                                                                                    name='new-work-order-pretest-scale'
                                                                                    className='custom-control-input'
                                                                                    value={false}
                                                                                    checked={!pretestOptionScaleToFit}
                                                                                    onChange={handlePretestOptionIsMultipleChange}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`new-work-order-pretest-scale-false`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    No
                                                                                </label>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </FadeIn> : null
                                                }
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_CREATE_OPTIONS) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label className='required' htmlFor='new-work-order-prescription-create-type'><small>Type</small></label>
                                                            <select
                                                                id='new-work-order-prescription-create-type'
                                                                className='custom-select form-control'
                                                                value={prescriptionCreateType ? prescriptionCreateType : ''}
                                                                onChange={handlePrescriptionCreateTypeChange}
                                                            >
                                                                <option value=''></option>
                                                                {
                                                                    prescriptionTypes ?
                                                                        prescriptionTypes.map((t, ti) => {
                                                                            return <option
                                                                                key={`new_work_order_prescription_create_type_${ti}`}
                                                                                value={t.id}
                                                                            >{t.name}</option>
                                                                        }) : null
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                {
                                                    prescriptionCreateType ?
                                                        <FadeIn>
                                                            <div className='row options'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-control-options'><small>{prescriptionCreateType && prescriptionTypes && prescriptionTypes.length > 0 ? `${prescriptionTypes.filter(t => t.id === prescriptionCreateType)[0].name} prescription` : 'Prescription'} mapping</small></label>
                                                                        {renderPrescriptionCreateMapping()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </FadeIn> : null
                                                }
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_COPY_OPTIONS) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label className='required' htmlFor='new-work-order-prescription-copy-type'><small>Type</small></label>
                                                            <select
                                                                id='new-work-order-prescription-copy-type'
                                                                className='custom-select form-control'
                                                                value={prescriptionCopyType ? prescriptionCopyType : ''}
                                                                onChange={handlePrescriptionCopyTypeChange}
                                                            >
                                                                <option value=''></option>
                                                                {
                                                                    prescriptionTypes ?
                                                                        prescriptionTypes.map((t, ti) => {
                                                                            return <option
                                                                                key={`new_work_order_prescription_copy_type_${ti}`}
                                                                                value={t.id}
                                                                            >{t.name}</option>
                                                                        }) : null
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                {
                                                    prescriptionCopyType ?
                                                        <FadeIn>
                                                            <div className='row options'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-control-options'><small>{prescriptionCopyType && prescriptionTypes && prescriptionTypes.length > 0 ? `${prescriptionTypes.filter(t => t.id === prescriptionCopyType)[0].name} prescription` : 'Prescription'} mapping</small></label>
                                                                        {renderPrescriptionCopyMapping()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </FadeIn> : null
                                                }
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_LIST_OPTIONS) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label className='required' htmlFor='new-work-order-prescription-list-type'><small>Type</small></label>
                                                            <select
                                                                id='new-work-order-prescription-create-list'
                                                                className='custom-select form-control'
                                                                value={prescriptionListType ? prescriptionListType : ''}
                                                                onChange={handlePrescriptionListTypeChange}
                                                            >
                                                                <option value=''></option>
                                                                {
                                                                    prescriptionTypes ?
                                                                        prescriptionTypes.map((t, ti) => {
                                                                            return <option
                                                                                key={`new_work_order_prescription_list_type_${ti}`}
                                                                                value={t.id}
                                                                            >{t.name}</option>
                                                                        }) : null
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-2'>
                                                            <label className='required'><small>Show</small></label>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-list-mode-all'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-list-mode'
                                                                    className='custom-control-input'
                                                                    checked={prescriptionListMode === 'all'}
                                                                    onChange={(e) => { handlePrescriptionListModeChange(e, 'all') }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-list-mode-all'
                                                                    className='custom-control-label'
                                                                >
                                                                    All prescription(s)
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-list-mode-previous'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-list-mode'
                                                                    className='custom-control-input'
                                                                    checked={prescriptionListMode === 'previous'}
                                                                    onChange={(e) => { handlePrescriptionListModeChange(e, 'previous') }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-list-mode-previous'
                                                                    className='custom-control-label'
                                                                >
                                                                    Previous prescription(s)
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-list-mode-exam'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-list-mode'
                                                                    className='custom-control-input'
                                                                    checked={prescriptionListMode === 'exam'}
                                                                    onChange={(e) => { handlePrescriptionListModeChange(e, 'exam') }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-list-mode-exam'
                                                                    className='custom-control-label'
                                                                >
                                                                    Exam prescription(s)
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRESCRIPTION_SUMMARY_OPTIONS) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-2'>
                                                            <label className='required'><small>Show</small></label>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-summary-mode-all'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-summary-mode'
                                                                    className='custom-control-input'
                                                                    checked={prescriptionListMode === 'all'}
                                                                    onChange={(e) => { handlePrescriptionSummaryModeChange(e, 'all') }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-summary-mode-all'
                                                                    className='custom-control-label'
                                                                >
                                                                    All prescription(s)
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-summary-mode-previous'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-summary-mode'
                                                                    className='custom-control-input'
                                                                    checked={prescriptionListMode === 'previous'}
                                                                    onChange={(e) => { handlePrescriptionSummaryModeChange(e, 'previous') }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-summary-mode-previous'
                                                                    className='custom-control-label'
                                                                >
                                                                    Previous prescription(s)
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-summary-mode-exam'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-summary-mode'
                                                                    className='custom-control-input'
                                                                    checked={prescriptionListMode === 'exam'}
                                                                    onChange={(e) => { handlePrescriptionSummaryModeChange(e, 'exam') }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-summary-mode-exam'
                                                                    className='custom-control-label'
                                                                >
                                                                    Exam prescription(s)
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-2'>
                                                            <label className='required'><small>Collapsible</small></label>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-summary-collapsible-multiple'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-summary-collapsible'
                                                                    className='custom-control-input'
                                                                    checked={!!prescriptionSummaryMultiple}
                                                                    onChange={(e) => { handlePrescriptionSummaryMultipleChange(e, true) }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-summary-collapsible-multiple'
                                                                    className='custom-control-label'
                                                                >
                                                                    Allow multiple prescription type(s) open
                                                                </label>
                                                            </div>
                                                            <div className='custom-control custom-radio mb-2'>
                                                                <input
                                                                    id='new-work-order-prescription-summary-collapsible-single'
                                                                    type='radio'
                                                                    name='new-work-order-prescription-summary-collapsible'
                                                                    className='custom-control-input'
                                                                    checked={!prescriptionSummaryMultiple}
                                                                    onChange={(e) => { handlePrescriptionSummaryMultipleChange(e, false) }}
                                                                />
                                                                <label
                                                                    htmlFor='new-work-order-prescription-summary-collapsible-single'
                                                                    className='custom-control-label'
                                                                >
                                                                    Only one prescription type can open at a time
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_PRODUCT_COPY_OPTIONS) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label className='required' htmlFor='new-work-order-product-copy-type'><small>Type</small></label>
                                                            <select
                                                                id='new-work-order-product-copy-type'
                                                                className='custom-select form-control'
                                                                value={productCopyType ? productCopyType : ''}
                                                                onChange={handleProductCopyTypeChange}
                                                            >
                                                                <option value=''></option>
                                                                {
                                                                    productTypes ?
                                                                        productTypes.map((t, ti) => {
                                                                            return <option
                                                                                key={`new_work_order_product_copy_type_${ti}`}
                                                                                value={t.id}
                                                                            >{t.name}</option>
                                                                        }) : null
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                {
                                                    productCopyType ?
                                                        <FadeIn>
                                                            <div className='row options'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-control-options'><small>{productCopyType && productTypes && productTypes.length > 0 ? `${productTypes.filter(t => t.id === productCopyType)[0].name} product` : 'Product'} mapping</small></label>
                                                                        {renderProductCopyMapping()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </FadeIn> : null
                                                }
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_FIELDS_EQUATIONS) ?
                                            <>
                                                <div className='row'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label className='required' htmlFor='new-work-order-fields-e'><small>Type</small></label>
                                                            <select
                                                                id='new-work-order-product-copy-type'
                                                                className='custom-select form-control'
                                                                value={productCopyType ? productCopyType : ''}
                                                                onChange={handleProductCopyTypeChange}
                                                            >
                                                                <option value=''></option>
                                                                {
                                                                    productTypes ?
                                                                        productTypes.map((t, ti) => {
                                                                            return <option
                                                                                key={`new_work_order_product_copy_type_${ti}`}
                                                                                value={t.id}
                                                                            >{t.name}</option>
                                                                        }) : null
                                                                }
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                {
                                                    productCopyType ?
                                                        <FadeIn>
                                                            <div className='row options'>
                                                                <div className='col-12'>
                                                                    <div className='form-group mb-4'>
                                                                        <label className='required' htmlFor='new-work-order-control-product-copy'><small>{productCopyType && productTypes && productTypes.length > 0 ? `${productTypes.filter(t => t.id === productCopyType)[0].name} product` : 'Product'} mapping</small></label>
                                                                        {renderProductCopyMapping()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </FadeIn> : null
                                                }
                                            </> : null
                                    }
                                    {
                                        tch.hasField(workOrderControl.data.type, tch.TEMPLATE_CONTROL_METADATA_SET_FIELDS_OPTIONS) ?
                                            <>
                                                <div className='row options'>
                                                    <div className='col-12'>
                                                        <div className='form-group mb-4'>
                                                            <label className='required' htmlFor='new-work-order-control-set-fields'><small>Field mappings</small></label>
                                                            {renderSetFieldMapping()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </> : null
                                    }
                                </section>
                            </div>
                        </FadeIn>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-12'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-link btn-cancel mr-2'
                                        onClick={handleCancel}
                                    >Cancel</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    </>)
}

export default UpdateExistingWorkOrderControl;